import React, { useCallback, useEffect, useState, useRef } from "react";
import { useParams } from 'react-router-dom';
import MediaPlayer from "./components/MediaPlayer";
import CallInfo from "./components/CallInfo";
import ListCalls from "./components/ListCalls";
import { useSelector, useDispatch } from 'react-redux'
import { useGetTalkgroupsQuery } from '../features/api/apiSlice'
import { playedCall, nextCallToPlay  } from "../features/calls/callsSlice";
import useLiveScroll from "./useLiveScroll";
import { useInView } from 'react-intersection-observer';
import {
  Container,
  Rail,
  Sticky,
  Menu,
  Icon,
  Sidebar,
  Message,
  MessageHeader
} from "semantic-ui-react";
import "./CallPlayer.css";
import queryString from '../query-string';
import io from 'socket.io-client';
import { useCallLink } from "./components/CallLinks";
import "./CallPlayer.css";
import { set } from "date-fns";


const socket = io(process.env.REACT_APP_BACKEND_SERVER);


// ----------------------------------------------------
function CallPlayer(props) {

  const { shortName } = useParams();
  const system = props.system;
  const selectCallId = props.selectCallId;
  const callsData = props.callsData;
  const handleNewer = props.handleNewer;
  const handleOlder = props.handleOlder;
  const { ref: loadOlderRef, inView: loadOlderInView } = useInView({
    // Start fetching before the user actually reaches the end of the list.
    rootMargin: '400px 0px'
  });
  const { ref: loadNewerRef, inView: loadNewerInView } = useInView();

  const live = props.live;
  const callsLoading = useSelector((state) => state.calls.loading);
  const hasMoreOlder = useSelector((state) => state.calls.hasMoreOlder);
  const hasMoreNewer = useSelector((state) => state.calls.hasMoreNewer);
  const backgroundAutoplay = useSelector((state) => state.callPlayer.backgroundAutoplay);
  const { data: talkgroupsData, isSuccess: isTalkgroupsSuccess } = useGetTalkgroupsQuery(shortName);
  const [statusVisible, setStatusVisible] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const [currentCall, setCurrentCall] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [silenceCount, setSilenceCount] = useState(0);
  const [selectedInitialCall, setSelectedInitialCall] = useState(false);
  const { callLink, callDownload, callTweet } = useCallLink(currentCall)

  const dispatch = useDispatch();
  const positionRef = useRef(); // context for the Sticky call info pane
  const listRef = useRef(); // the call list table, so useLiveScroll can find rows
  const shouldPlayAddCallRef = useRef(); // we need to do this to make the current value of isPlaying available in the socket message callback
  //const selectedInitialCall = useRef(false); // allows you to track if you have already selected the Initial Call - you only want to do this once
  shouldPlayAddCallRef.current = (!isPlaying && autoPlay) ? true : false;

  let currentCallId = false;

  if (currentCall) {
    currentCallId = currentCall._id;
  }

  // All scroll handling for the list - holding the user's position when calls
  // are inserted above them, following the playing call, and the "new calls"
  // banner when they have scrolled away from live.
  const { following, newCallCount, jumpToLive } = useLiveScroll({
    listRef,
    callsData,
    activeCallId: currentCallId,
    activeCallTime: currentCall ? currentCall.time : null
  });

  const handlePlayPause = (playing) => {
    setIsPlaying(playing);
  }

  const handleAutoPlay = (currentAutoPlay) => {
    setAutoPlay(!currentAutoPlay);
  }

  // Memoised so that CallItem's React.memo holds: without a stable onClick every
  // row in the list re-renders each time a call arrives.
  const playCall = useCallback((data) => {
    setCurrentCall(data.call);
    dispatch(playedCall(data.call._id));
    setIsPlaying(true);
  }, [dispatch])


  /* This function gets called whenever the currently playing call ends.*/

  const callEnded = () => {
    if (callsData) {
      if (autoPlay) {
        const nextCall = nextCallToPlay(callsData, currentCallId);
        if (nextCall) {
          console.log("Autoplaying next call: " + nextCall._id + " isPlaying is: " + isPlaying )
          setCurrentCall(nextCall);
          dispatch(playedCall(nextCall._id));
          setIsPlaying(true);
        } else if (backgroundAutoplay) {
          // there are no more calls to play, but we want to keep playing something so the webpage is not put to sleep, so we play silence!
          setSilenceCount(silenceCount+1);
        } else {
          // there are no more calls to play and backgroundAutoplay is disabled
          setIsPlaying(false);
        }
      } else {
        console.log("Not playing because Autoplay is false")
        setIsPlaying(false);
      }
    } else {
      console.log("Somehow called, callEnded() but callsData was false");
    }
  }

  // Paging. These depend on callsData and callsLoading as well as the sentinel
  // being in view, so that a page which arrives too short to push the sentinel
  // back out of the viewport triggers the next one instead of stalling until
  // the user scrolls away and back. hasMore stops that becoming a loop once
  // there is nothing left to fetch.
  useEffect(() => {
    if (loadNewerInView && !live && !callsLoading && hasMoreNewer && callsData && (callsData.ids.length > 0)) {
      handleNewer();
    }
  }, [loadNewerInView, live, callsLoading, hasMoreNewer, callsData]);


  useEffect(() => {
    if (loadOlderInView && !callsLoading && hasMoreOlder && callsData && (callsData.ids.length > 0)) {
      handleOlder();
    }
  }, [loadOlderInView, callsLoading, hasMoreOlder, callsData]);

  // This handles when a Call is specified in the URI
useEffect(() => {
  if (props.initialCallId && callsData) {
    const call = callsData.entities[props.initialCallId];
    if (call && !selectedInitialCall) {
      const time = new Date(call.time);

      console.log("Playing Initial Call: " + call._id + " Start Time: " + time.toLocaleTimeString() );
      setIsPlaying(true);  
      setCurrentCall(call); 
      setSelectedInitialCall(true); // signals that it has selected the initial call - you only want to do this once
      dispatch(playedCall(call._id));
    }
  }
}, [props.initialCallId, callsData])

  // Triggered when a new call is selected in the parent component.
  // This happens when a new call comes over the socket
  // When a call is set to CurrentCall, it will automatically start playing
  // we should only set the selectCallId to be the current call when AutoPlay is selected
  // and when there isn't another call already playing
  useEffect(() => {
    console.log("New selected call: " + selectCallId + " IsPlaying: " + isPlaying + " autoPlay: " + autoPlay );
    if (selectCallId && callsData && !isPlaying && autoPlay) {
      const call = callsData.entities[selectCallId];
      if (call) {
        const time = new Date(call.time);

        console.log("Playing Selected Call: " + call._id + " Start Time: " + time.toLocaleTimeString() );
        setIsPlaying(true);
        setCurrentCall(call);
        dispatch(playedCall(call._id));
      }
    }
  }, [selectCallId])

  useEffect(() => {
    if (system.status && system.status.length > 0) {
        setStatusVisible(true);
    }
  }, [system])

  const handleStatusDismiss = () => {
    setStatusVisible(false);
  }

  return (
    <div ref={positionRef}>
      {!following && newCallCount > 0 &&
        <div className="new-calls-banner" onClick={jumpToLive}>
          <Icon name="arrow up" />
          {newCallCount === 1 ? "New call" : `${newCallCount} new calls`}
        </div>
      }
      <Container className="main" >
        {statusVisible &&
      <Sticky offset={60} context={positionRef}>
            <Message floating onDismiss={handleStatusDismiss} warning> <MessageHeader>System Status</MessageHeader>{system.status}</Message>
            </Sticky>
        }
        <Sidebar.Pushable>
          <Sidebar.Pusher
            style={{ minHeight: '100vh' }}
          >

            <div ref={loadNewerRef} />

            <ListCalls listRef={listRef} callsData={callsData} activeCallId={currentCallId} playCall={playCall} />
            <div ref={loadOlderRef} style={{ height: 50 }} />
          </Sidebar.Pusher>
        </Sidebar.Pushable>
        <Rail position='right' className="desktop-only"  >
          <Sticky offset={60} context={positionRef}>
            <CallInfo call={currentCall} />
          </Sticky>
        </Rail>
      </Container>

      <Menu fixed="bottom" compact inverted >
        <Menu.Item active={autoPlay} onClick={() => handleAutoPlay(autoPlay)}><Icon name="level up" /><span className="desktop-only">Autoplay</span></Menu.Item>
        <MediaPlayer call={currentCall} playSilence={silenceCount} onEnded={callEnded} onPlayPause={handlePlayPause} />
        <Menu.Menu position="right" className="desktop-only">
          <Menu.Item><a href={callDownload}><Icon name="download" />Download</a></Menu.Item>
          <Menu.Item><a href={callLink}><Icon name="at" />Link</a></Menu.Item>
        </Menu.Menu>
      </Menu>

    </div>
  );
}


export default CallPlayer;
